import { useSharedAPI } from "@certn/api";
import { Key, useMemo, FocusEvent } from "react";
import { useTranslation } from "react-i18next";

import { SelectItem } from "../../components/inputs/select/SelectItem";
import { Select } from "../../components/inputs/select/Select";

import { FieldErrorsProps } from "../../components/inputs/_assets/input-wrapper/FieldErrors";

type AdministrativeDivisionSelectProps = {
  label: string;
  country?: string;
  testID: string;
  onChange?: ((key: Key | null) => void) | undefined;
  selectedKey?: string;
  errors?: FieldErrorsProps["children"];
  isInvalid?: boolean;
  onBlur?: (e: FocusEvent<Element>) => void;
  isDisabled?: boolean;
  placeholder?: string;
  description?: string;
  isRequired?: boolean;
  emptyMessage?: string;
};

export function AdministrativeDivisionSelect(
  props: AdministrativeDivisionSelectProps
) {
  const { t } = useTranslation();
  const {
    label,
    country,
    onChange,
    testID = "administrative-division-select",
    selectedKey,
    errors,
    isInvalid,
    onBlur,
    isDisabled,
    placeholder = `${t("Select")} ${label.split(" ")[0]?.toLowerCase()}`,
    description,
    isRequired = true,
    emptyMessage = t("No results found"),
  } = props;
  const { data: subdivisions, isLoading } = useSharedAPI(
    "/api/internal/shared/country-subdivisions/",
    {
      alpha_2: country || "CA",
    }
  );

  const sortedSubdivisions = useMemo(() => {
    const subdivisionKeysGB = ["Country", "Province", "State", "Territory"];
    const filtered = subdivisions
      ?.filter((subdivision) => {
        if (country === "GB") {
          return subdivisionKeysGB.includes(subdivision.type);
        }
        return true;
      })
      .map((subdivision) => ({
        ...subdivision,
        name: subdivision.name.split(";")[0].split("[")[0], // cleanup names (ex. "Wales; Cymru" & "Wales [Cymru GB-CYM]") Go Wales!!! 🏴󠁧󠁢󠁷󠁬󠁳󠁿
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return filtered;
  }, [subdivisions, country]);

  return (
    <Select
      testID={testID}
      label={label}
      isRequired={isRequired}
      isDisabled={isLoading || !sortedSubdivisions?.length || isDisabled}
      placeholder={placeholder}
      onSelectionChange={onChange}
      selectedKey={selectedKey ?? ""}
      items={sortedSubdivisions}
      errors={errors}
      isInvalid={isInvalid}
      onBlur={onBlur}
      validationBehavior={country === "AU" ? "native" : "aria"}
      description={description}
      emptyMessage={emptyMessage}
    >
      {(item) => (
        <SelectItem key={item.code} id={item.code} textValue={item.name}>
          {item.name}
        </SelectItem>
      )}
    </Select>
  );
}
